import { cn } from '@/lib/utils'
import { cva, VariantProps } from 'class-variance-authority'
import { Star } from 'lucide-react'
import colors from 'tailwindcss/colors'

const starRatingVariants = cva('text-gray-900', {
  variants: {
    size: {
      sm: 'h-2 w-2',
      md: 'h-4 w-4',
      lg: 'h-8 w-8',
    },
  },
  defaultVariants: {
    size: 'md',
  },
})

export interface StarRatingProps
  extends VariantProps<typeof starRatingVariants> {
  rating: number
  count?: number
  starsOnly?: boolean
  className?: string
  size?: 'sm' | 'md' | 'lg'
}

const StarRating = ({
  rating,
  count,
  starsOnly,
  className,
  size,
}: StarRatingProps) => {
  return (
    <div>
      <h4 className="sr-only">StarRating</h4>
      <div className={cn('mt-2 flex items-center space-x-2', className)}>
        <div className="relative">
          <div className="flex w-full items-center space-x-1">
            {Array.from({ length: 5 }).map((_, index) => {
              return (
                <Star
                  key={index}
                  className={cn(starRatingVariants({ size }))}
                />
              )
            })}
          </div>
          <div className="absolute top-0 flex w-full space-x-1 overflow-clip">
            {Array.from({ length: Math.ceil(rating) }).map((_, index) => {
              return index !== Math.floor(rating) ? (
                <Star
                  key={index}
                  className={cn('shrink-0', starRatingVariants({ size }))}
                  fill={colors.gray[900]}
                />
              ) : (
                <Star
                  key={index}
                  className={cn('shrink-0', starRatingVariants({ size }))}
                  style={{
                    maskImage: `linear-gradient(to right, #000 0%, #000 ${(rating % 1) * 100}%, transparent ${(rating % 1) * 100}%, transparent 100%)`,
                  }}
                  fill={colors.gray[600]}
                />
              )
            })}
          </div>
        </div>

        {!starsOnly && (
          <div className="flex h-full items-center space-x-1">
            <span className="text-xs text-secondary-900">
              {rating.toFixed(1)}
            </span>
            {Number(count) > 0 && (
              <span className="text-xs text-muted-foreground">({count})</span>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default StarRating
